import { CommonModule } from '@angular/common';
import { Component, HostListener, OnDestroy } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { GlameraAnimationMode, GlameraIconComponent, GlameraIconPosition } from '@glamera/animated-icons';
import { MoonIconComponent } from '@glamera/animated-icons/moon';
import { SunIconComponent } from '@glamera/animated-icons/sun';
import { RepeatIconComponent } from '@glamera/animated-icons/repeat';
import { CopyIconComponent } from '@glamera/animated-icons/copy';

export interface DemoIcon {
  name: string;
  label: string;
  animation: string;
  entry: string;
  exportName: string;
}

export interface DemoAnimationMode {
  mode: GlameraAnimationMode;
  label: string;
  hint: string;
}

export type DemoTab = 'icons' | 'docs';

function resolveSystemTheme(): 'light' | 'dark' {
  if (typeof window === 'undefined') {
    return 'light';
  }

  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function resolveInitialTheme(): 'light' | 'dark' {
  if (typeof document === 'undefined') {
    return resolveSystemTheme();
  }

  const existingTheme = document.documentElement.getAttribute('data-theme');
  return existingTheme === 'light' || existingTheme === 'dark' ? existingTheme : resolveSystemTheme();
}

export interface DemoTabOption {
  id: DemoTab;
  label: string;
}

@Component({
  standalone: true,
  selector: 'app-demo',
  imports: [CommonModule, FormsModule, GlameraIconComponent, SunIconComponent, MoonIconComponent, RepeatIconComponent, CopyIconComponent],
  templateUrl: './demo.component.html',
  styleUrls: ['./demo.component.scss'],
})
export class DemoComponent implements OnDestroy {
  readonly icons: DemoIcon[] = [
    { name: 'search', label: 'Search', animation: 'Lens search / rotate', entry: 'search', exportName: 'SearchIcon' },
    { name: 'search-dollar', label: 'Search Dollar', animation: 'Lens scan / dollar pop', entry: 'search-dollar', exportName: 'SearchDollarIcon' },
    { name: 'heart', label: 'Heart', animation: 'Stroke draw / pulse', entry: 'heart', exportName: 'HeartIcon' },
    { name: 'heart-pulse', label: 'Heart Pulse', animation: 'Beat / ECG line stretch', entry: 'heart-pulse', exportName: 'HeartPulseIcon' },
    { name: 'heart-minus', label: 'Heart Minus', animation: 'Staggered scale pop', entry: 'heart-minus', exportName: 'HeartMinusIcon' },
    { name: 'heart-plus', label: 'Heart Plus', animation: 'Staggered scale pop', entry: 'heart-plus', exportName: 'HeartPlusIcon' },
    { name: 'heart-off', label: 'Heart Off', animation: 'Staggered scale pop', entry: 'heart-off', exportName: 'HeartOffIcon' },
    { name: 'heart-handshake', label: 'Heart Handshake', animation: 'Heartbeat', entry: 'heart-handshake', exportName: 'HeartHandshakeIcon' },
    { name: 'hand-heart', label: 'Hand Heart', animation: 'Heart pop / hand lift', entry: 'hand-heart', exportName: 'HandHeartIcon' },
    { name: 'bell-ring', label: 'Bell Ring', animation: 'Ring / wave', entry: 'bell', exportName: 'BellRingIcon' },
    { name: 'book-heart', label: 'Book Heart', animation: 'Staggered scale pop', entry: 'book-heart', exportName: 'BookHeartIcon' },
    { name: 'building-office', label: 'Building Office', animation: 'Window floor fade', entry: 'building-office', exportName: 'BuildingOfficeIcon' },
    { name: 'check', label: 'Check', animation: 'Tick draw / scale pop', entry: 'check', exportName: 'CheckIcon' },
    { name: 'check-check', label: 'Check Check', animation: 'Staggered tick draw', entry: 'check-check', exportName: 'CheckCheckIcon' },
    { name: 'circle-check-big', label: 'Circle Check Big', animation: 'Draw / pulse', entry: 'circle-check-big', exportName: 'CircleCheckBigIcon' },
    { name: 'settings', label: 'Settings', animation: 'Gear rotate', entry: 'settings', exportName: 'SettingsIcon' },
    { name: 'moon', label: 'Moon', animation: 'Float / glow', entry: 'moon', exportName: 'MoonIcon' },
    { name: 'sun', label: 'Sun', animation: 'Rotate / pulse', entry: 'sun', exportName: 'SunIcon' },
    { name: 'smartphone', label: 'Smartphone', animation: 'Buzz / home-dot pulse', entry: 'smartphone', exportName: 'SmartphoneIcon' },
    { name: 'star', label: 'Star', animation: 'Scale bounce', entry: 'star', exportName: 'StarIcon' },
    { name: 'store', label: 'Store', animation: 'Awning sway / door lift', entry: 'store', exportName: 'StoreIcon' },
    { name: 'shopping-cart', label: 'Shopping Cart', animation: 'Cart nudge / wheel bounce', entry: 'shopping-cart', exportName: 'ShoppingCartIcon' },
    { name: 'repeat', label: 'Repeat', animation: 'Arc pulse / head bounce', entry: 'repeat', exportName: 'RepeatIcon' },
    { name: 'rotate-ccw', label: 'Rotate CCW', animation: 'Spin / scale pulse', entry: 'rotate-ccw', exportName: 'RotateCcwIcon' },
    { name: 'rotate-cw', label: 'Rotate CW', animation: 'Spin / scale pulse', entry: 'rotate-cw', exportName: 'RotateCwIcon' },
    { name: 'refresh-cw', label: 'Refresh CW', animation: 'Staggered spin', entry: 'refresh-cw', exportName: 'RefreshCwIcon' },
    { name: 'mail', label: 'Mail', animation: 'Flap draw / bounce', entry: 'mail', exportName: 'MailIcon' },
    { name: 'message-circle-more', label: 'Message Circle More', animation: 'Bubble pop / dot bounce', entry: 'message-circle-more', exportName: 'MessageCircleMoreIcon' },
    { name: 'message-circle-heart', label: 'Message Circle Heart', animation: 'Bubble pop / heart beat', entry: 'message-circle-heart', exportName: 'MessageCircleHeartIcon' },
    { name: 'lock', label: 'Lock', animation: 'Shake / rotate wiggle', entry: 'lock', exportName: 'LockIcon' },
    { name: 'lock-open', label: 'Lock Open', animation: 'Scale pulse / shackle lift', entry: 'lock-open', exportName: 'LockOpenIcon' },
    { name: 'eye', label: 'Eye', animation: 'Blink / pupil scan', entry: 'eye', exportName: 'EyeIcon' },
    { name: 'eye-closed', label: 'Eye Closed', animation: 'Squeeze / lash flick', entry: 'eye-closed', exportName: 'EyeClosedIcon' },
    { name: 'eye-off', label: 'Eye Off', animation: 'Fade / slash draw', entry: 'eye-off', exportName: 'EyeOffIcon' },
    { name: 'copy', label: 'Copy', animation: 'Layer split / spin', entry: 'copy', exportName: 'CopyIcon' },
    { name: 'credit-card', label: 'Credit Card', animation: 'Line erase / redraw', entry: 'credit-card', exportName: 'CreditCardIcon' },
    { name: 'wallet', label: 'Wallet', animation: 'Draw / snap pop', entry: 'wallet', exportName: 'WalletIcon' },
    { name: 'clipboard', label: 'Clipboard', animation: 'Stroke draw / lift', entry: 'clipboard', exportName: 'ClipboardIcon' },
    { name: 'file', label: 'File', animation: 'Document pop', entry: 'file', exportName: 'FileIcon' },
    { name: 'files', label: 'Files', animation: 'Staggered stroke draw', entry: 'files', exportName: 'FilesIcon' },
    { name: 'fingerprint', label: 'Fingerprint', animation: 'Staggered fade pop', entry: 'fingerprint', exportName: 'FingerprintIcon' },
    { name: 'file-heart', label: 'File Heart', animation: 'File pop / heart beat', entry: 'file-heart', exportName: 'FileHeartIcon' },
    { name: 'file-spreadsheet', label: 'File Spreadsheet', animation: 'File pop / cells draw', entry: 'file-spreadsheet', exportName: 'FileSpreadsheetIcon' },
    { name: 'file-invoice', label: 'File Invoice', animation: 'File pop / lines + box draw', entry: 'file-invoice', exportName: 'FileInvoiceIcon' },
    { name: 'folder-heart', label: 'Folder Heart', animation: 'Staggered scale pop', entry: 'folder-heart', exportName: 'FolderHeartIcon' },
    { name: 'file-x', label: 'File X', animation: 'Body pop / cross spin', entry: 'file-x', exportName: 'FileXIcon' },
    { name: 'history', label: 'History', animation: 'Minute rewind / arrow nudge', entry: 'history', exportName: 'HistoryIcon' },
    { name: 'house', label: 'House', animation: 'Pop / door reveal', entry: 'house', exportName: 'HouseIcon' },
    { name: 'house-heart', label: 'House Heart', animation: 'Staggered scale pop', entry: 'house-heart', exportName: 'HouseHeartIcon' },
    { name: 'identification', label: 'Identification', animation: 'Staggered stroke draw', entry: 'identification', exportName: 'IdentificationIcon' },
    { name: 'phone', label: 'Phone', animation: 'Ring / pulse', entry: 'phone', exportName: 'PhoneIcon' },
    { name: 'user', label: 'User', animation: 'Head pop / body draw', entry: 'user', exportName: 'UserIcon' },
    { name: 'user-round', label: 'User Round', animation: 'Head pop / curve draw', entry: 'user-round', exportName: 'UserRoundIcon' },
    { name: 'user-check', label: 'User Check', animation: 'Head / body / tick draw', entry: 'user-check', exportName: 'UserCheckIcon' },
    { name: 'user-round-check', label: 'User Round Check', animation: 'Head / curve / tick draw', entry: 'user-round-check', exportName: 'UserRoundCheckIcon' },
    { name: 'user-x', label: 'User X', animation: 'Head / body / cross shake', entry: 'user-x', exportName: 'UserXIcon' },
    { name: 'user-plus', label: 'User Plus', animation: 'Head / body / plus shake', entry: 'user-plus', exportName: 'UserPlusIcon' },
    { name: 'user-round-x', label: 'User Round X', animation: 'Head / curve / cross shake', entry: 'user-round-x', exportName: 'UserRoundXIcon' },
    { name: 'users', label: 'Users', animation: 'Head pop / staggered draw', entry: 'users', exportName: 'UsersIcon' },
    { name: 'user-group', label: 'User Group', animation: 'Side users slide in', entry: 'user-group', exportName: 'UserGroupIcon' },
    { name: 'qr-code', label: 'QR Code', animation: 'Pulse / block shift / bit flicker', entry: 'qr-code', exportName: 'QrCodeIcon' },
    { name: 'scan-qr-code', label: 'Scan QR Code', animation: 'Scan pulse / staggered bits', entry: 'scan-qr-code', exportName: 'ScanQrCodeIcon' },
    { name: 'scan-heart', label: 'Scan Heart', animation: 'Staggered scale pop', entry: 'scan-heart', exportName: 'ScanHeartIcon' },
    { name: 'trash', label: 'Trash', animation: 'Lid open / bin squash', entry: 'trash', exportName: 'TrashIcon' },
    { name: 'trash-2', label: 'Trash 2', animation: 'Lid open / bars drop / bin squash', entry: 'trash-2', exportName: 'Trash2Icon' },
    { name: 'upload', label: 'Upload', animation: 'Arrow rise / tray draw', entry: 'upload', exportName: 'UploadIcon' },
    { name: 'download', label: 'Download', animation: 'Arrow drop / tray draw', entry: 'download', exportName: 'DownloadIcon' },
    { name: 'dumbbell', label: 'Dumbbell', animation: 'Lift rotate / scale', entry: 'dumbbell', exportName: 'DumbbellIcon' },
    { name: 'download-cloud', label: 'Download Cloud', animation: 'Arrow bounce', entry: 'download-cloud', exportName: 'DownloadCloudIcon' },
    { name: 'upload-cloud', label: 'Upload Cloud', animation: 'Arrow bounce', entry: 'upload-cloud', exportName: 'UploadCloudIcon' },
    { name: 'save', label: 'Save', animation: 'Shutter slide / label lift', entry: 'save', exportName: 'SaveIcon' },
    { name: 'chevron-right', label: 'Chevron Right', animation: 'Nudge / trail slide', entry: 'chevron-right', exportName: 'ChevronRightIcon' },
    { name: 'chevron-left', label: 'Chevron Left', animation: 'Nudge / trail slide', entry: 'chevron-left', exportName: 'ChevronLeftIcon' },
    { name: 'arrow-left', label: 'Arrow Left', animation: 'Horizontal nudge', entry: 'arrow-left', exportName: 'ArrowLeftIcon' },
    { name: 'arrow-left-right', label: 'Arrow Left Right', animation: 'Opposite horizontal nudge', entry: 'arrow-left-right', exportName: 'ArrowLeftRightIcon' },
    { name: 'arrow-up-down', label: 'Arrow Up Down', animation: 'Opposite vertical nudge', entry: 'arrow-up-down', exportName: 'ArrowUpDownIcon' },
    { name: 'arrow-down-up', label: 'Arrow Down Up', animation: 'Opposite vertical nudge', entry: 'arrow-down-up', exportName: 'ArrowDownUpIcon' },
    { name: 'arrow-down-to-line', label: 'Arrow Down To Line', animation: 'Arrow bounce / line tap', entry: 'arrow-down-to-line', exportName: 'ArrowDownToLineIcon' },
    { name: 'arrow-right', label: 'Arrow Right', animation: 'Horizontal nudge', entry: 'arrow-right', exportName: 'ArrowRightIcon' },
    { name: 'chevron-up', label: 'Chevron Up', animation: 'Nudge / trail slide', entry: 'chevron-up', exportName: 'ChevronUpIcon' },
    { name: 'chevron-down', label: 'Chevron Down', animation: 'Nudge / trail slide', entry: 'chevron-down', exportName: 'ChevronDownIcon' },
    { name: 'circle-chevron-up', label: 'Circle Chevron Up', animation: 'Circle bounce / arrow rise', entry: 'circle-chevron-up', exportName: 'CircleChevronUpIcon' },
    { name: 'circle-chevron-right', label: 'Circle Chevron Right', animation: 'Circle bounce / arrow slide', entry: 'circle-chevron-right', exportName: 'CircleChevronRightIcon' },
    { name: 'circle-chevron-left', label: 'Circle Chevron Left', animation: 'Circle bounce / arrow slide', entry: 'circle-chevron-left', exportName: 'CircleChevronLeftIcon' },
    { name: 'circle-chevron-down', label: 'Circle Chevron Down', animation: 'Circle bounce / arrow drop', entry: 'circle-chevron-down', exportName: 'CircleChevronDownIcon' },
    { name: 'share', label: 'Share', animation: 'Node pop / line draw', entry: 'share', exportName: 'ShareIcon' },
    { name: 'screen-share', label: 'Screen Share', animation: 'Staggered scale pop', entry: 'screen-share', exportName: 'ScreenShareIcon' },
    { name: 'expand', label: 'Expand', animation: 'Corner breathe', entry: 'expand', exportName: 'ExpandIcon' },
    { name: 'shrink', label: 'Shrink', animation: 'Corner pull-in', entry: 'shrink', exportName: 'ShrinkIcon' },
    { name: 'circle-check', label: 'Circle Check', animation: 'Ring pulse / tick draw', entry: 'circle-check', exportName: 'CircleCheckIcon' },
    { name: 'circle-alert', label: 'Circle Alert', animation: 'Ring pulse / mark wiggle', entry: 'circle-alert', exportName: 'CircleAlertIcon' },
    { name: 'circle-question-mark', label: 'Circle Question Mark', animation: 'Ring pulse / curl wiggle / dot bounce', entry: 'circle-question-mark', exportName: 'CircleQuestionMarkIcon' },
    { name: 'layers', label: 'Layers', animation: 'Stack lift', entry: 'layers', exportName: 'LayersIcon' },
    { name: 'list-filter', label: 'List Filter', animation: 'Line squeeze / fade', entry: 'list-filter', exportName: 'ListFilterIcon' },
    { name: 'list-ordered', label: 'List Ordered', animation: 'Row squeeze / number pulse', entry: 'list-ordered', exportName: 'ListOrderedIcon' },
    { name: 'square-pen', label: 'Square Pen', animation: 'Pen wiggle', entry: 'square-pen', exportName: 'SquarePenIcon' },
    { name: 'clock', label: 'Clock', animation: 'Hand spin', entry: 'clock', exportName: 'ClockIcon' },
    { name: 'alarm-clock-check', label: 'Alarm Clock Check', animation: 'Wiggle / tick draw', entry: 'alarm-clock-check', exportName: 'AlarmClockCheckIcon' },
    { name: 'paperclip', label: 'Paperclip', animation: 'Stroke draw-through', entry: 'paperclip', exportName: 'PaperclipIcon' },
    { name: 'pencil', label: 'Pencil', animation: 'Write wiggle', entry: 'pencil', exportName: 'PencilIcon' },
    { name: 'square-check-big', label: 'Square Check Big', animation: 'Check draw', entry: 'square-check-big', exportName: 'SquareCheckBigIcon' },
    { name: 'play-circle', label: 'Play Circle', animation: 'Triangle bounce', entry: 'play-circle', exportName: 'PlayCircleIcon' },
    { name: 'x-circle', label: 'X Circle', animation: 'X draw', entry: 'x-circle', exportName: 'XCircleIcon' },
    { name: 'x', label: 'X', animation: 'Shake / line draw', entry: 'x', exportName: 'XIcon' },
    { name: 'plus', label: 'Plus', animation: 'Rotate / scale pop', entry: 'plus', exportName: 'PlusIcon' },
    { name: 'minus', label: 'Minus', animation: 'Rotate / scale spin', entry: 'minus', exportName: 'MinusIcon' },
    { name: 'monitor', label: 'Monitor', animation: 'Screen wiggle', entry: 'monitor', exportName: 'MonitorIcon' },
    { name: 'network', label: 'Network', animation: 'Nodes pulse / stem draw', entry: 'network', exportName: 'NetworkIcon' },
    { name: '3-dots-horizontal', label: '3 Dots Horizontal', animation: 'Dot bounce', entry: '3-dots-horizontal', exportName: 'ThreeDotsHorizontalIcon' },
    { name: '3-dots-vertical', label: '3 Dots Vertical', animation: 'Dot shift', entry: '3-dots-vertical', exportName: 'ThreeDotsVerticalIcon' },
    { name: 'calendar-x', label: 'Calendar X', animation: 'Draw / X pop', entry: 'calendar-x', exportName: 'CalendarXIcon' },
    { name: 'calendar', label: 'Calendar', animation: 'Draw / header reveal', entry: 'calendar', exportName: 'CalendarIcon' },
    { name: 'calendar-check', label: 'Calendar Check', animation: 'Draw / check pop', entry: 'calendar-check', exportName: 'CalendarCheckIcon' },
    { name: 'calendar-heart', label: 'Calendar Heart', animation: 'Draw / heart pop', entry: 'calendar-heart', exportName: 'CalendarHeartIcon' },
    { name: 'scan-text', label: 'Scan Text', animation: 'Line scan', entry: 'scan-text', exportName: 'ScanTextIcon' },
    { name: 'printer', label: 'Printer', animation: 'Paper feed', entry: 'printer', exportName: 'PrinterIcon' },
    { name: 'printer-2', label: 'Printer 2', animation: 'Fill / fade pop', entry: 'printer-2', exportName: 'Printer2Icon' },
    { name: 'table', label: 'Table', animation: 'Row shift / column sway', entry: 'table', exportName: 'TableIcon' },
    { name: 'columns-settings', label: 'Columns Settings', animation: 'Gear turn', entry: 'columns-settings', exportName: 'ColumnsSettingsIcon' },
  ];

  readonly animationModeOptions: DemoAnimationMode[] = [
    { mode: 'hover', label: 'Hover', hint: 'Plays once over icon or host padding' },
    { mode: 'hover-loop', label: 'Hover Loop', hint: 'Repeats while pointer is over host' },
    { mode: 'loop', label: 'Loop', hint: 'Plays continuously' },
    { mode: 'none', label: 'None', hint: 'Static, no animation' },
    { mode: 'click', label: 'Click', hint: 'Plays on each click' },
  ];

  readonly iconPositionOptions: { value: GlameraIconPosition; label: string }[] = [
    { value: 'start', label: 'Icon first' },
    { value: 'end', label: 'Icon last' },
  ];

  readonly tabs: DemoTabOption[] = [
    { id: 'icons', label: 'Icons' },
    { id: 'docs', label: 'Documentation' },
  ];

  activeTab: DemoTab = 'icons';
  searchQuery = '';
  selectedIcon = 'search';
  panelOpen = false;
  animation: GlameraAnimationMode = 'hover';
  size = 64;
  duration = 1000;
  color = '#7c3aed';
  gap = 8;
  iconPosition: GlameraIconPosition = 'start';
  playOnInit = false;
  showLabel = false;
  useCustomDimensions = false;
  customWidth = 64;
  customHeight = 64;
  theme: 'light' | 'dark' = resolveInitialTheme();
  hoveredGalleryIcon: string | null = null;
  hoveredThemeButton: 'light' | 'dark' | null = null;
  hoveredAnimationMode: GlameraAnimationMode | null = null;
  private themeManuallySet = false;
  private readonly systemThemeQuery =
    typeof window !== 'undefined' ? window.matchMedia('(prefers-color-scheme: dark)') : null;
  private readonly onSystemThemeChange = (event: MediaQueryListEvent): void => {
    if (this.themeManuallySet) {
      return;
    }

    this.theme = event.matches ? 'dark' : 'light';
    this.applyTheme(this.theme);
  };

  readonly installSnippet = 'npm install @glamera/animated-icons';
  copiedKey: string | null = null;
  copyHoverKey: string | null = null;
  private copyTimer: ReturnType<typeof setTimeout> | null = null;
  private bodyScrollY = 0;
  readonly usageTs = `import { Component } from '@angular/core';
import { EyeIconComponent } from '@glamera/animated-icons/eye';

@Component({
  standalone: true,
  imports: [EyeIconComponent],
  template: \`
    <glamera-eye-icon
      animation="hover"
      playOnInit
      size="16"
      gap="8"
      iconPosition="end"
      color="#5856d6"
      class="filters-btn"
      (click)="openFilters()">
      <span class="btn-label">{{ 'Filters' | translate }}</span>
    </glamera-eye-icon>
  \`,
})
export class FeatureComponent {}`;
  readonly usageHtml = `<glamera-eye-icon
  animation="hover"
  playOnInit
  size="16"
  gap="8"
  iconPosition="end"
  color="#5856d6"
  class="filters-btn">
  <span class="btn-label">Filters</span>
</glamera-eye-icon>`;
  readonly usageSizingHtml = `<glamera-mail-icon size="24"></glamera-mail-icon>
<glamera-mail-icon width="20" height="14"></glamera-mail-icon>`;
  readonly usageHostCss = `.filters-btn {
  display: inline-flex;
  align-items: center;
  min-height: 46px;
  padding: 0 18px;
  border: 1px solid #d0d5dd;
  border-radius: 12px;
  cursor: pointer;
}

.filters-btn:hover {
  background: #f1f1f1;
}`;

  constructor() {
    this.applyTheme(this.theme);
    this.systemThemeQuery?.addEventListener('change', this.onSystemThemeChange);
  }

  get selectedMeta(): DemoIcon {
    return this.icons.find((icon) => icon.name === this.selectedIcon) ?? this.icons[0];
  }

  get filteredIcons(): DemoIcon[] {
    const query = this.searchQuery.trim().toLowerCase();
    if (!query) {
      return this.icons;
    }

    return this.icons.filter(
      (icon) =>
        icon.name.toLowerCase().includes(query) ||
        icon.label.toLowerCase().includes(query) ||
        icon.entry.toLowerCase().includes(query),
    );
  }

  get durationFactorLabel(): string {
    return `${(this.duration / 1000).toFixed(2)}x`;
  }

  get previewAnimation(): GlameraAnimationMode {
    return this.hoveredAnimationMode ?? this.animation;
  }

  get customizedHtml(): string {
    const attrs = [
      `animation="${this.animation}"`,
      `size="${this.size}"`,
      `duration="${this.duration}"`,
    ];

    if (this.playOnInit) {
      attrs.push('playOnInit');
    }

    if (this.color !== '#7c3aed') {
      attrs.push(`color="${this.color}"`);
    }

    if (this.showLabel && this.gap) {
      attrs.push(`gap="${this.gap}"`);
    }

    if (this.showLabel && this.iconPosition !== 'start') {
      attrs.push(`iconPosition="${this.iconPosition}"`);
    }

    if (this.useCustomDimensions && this.customWidth) {
      attrs.push(`width="${this.customWidth}"`);
    }

    if (this.useCustomDimensions && this.customHeight) {
      attrs.push(`height="${this.customHeight}"`);
    }

    const open = `<glamera-${this.selectedIcon}-icon\n  ${attrs.join('\n  ')}`;
    if (this.showLabel) {
      return `${open}>\n  <span class="btn-label">${this.selectedMeta.label}</span>\n</glamera-${this.selectedIcon}-icon>`;
    }

    return `${open}>\n</glamera-${this.selectedIcon}-icon>`;
  }

  get customizedImport(): string {
    const icon = this.selectedMeta;
    return `import { ${icon.exportName}Component } from '@glamera/animated-icons/${icon.entry}';`;
  }

  get customizedUsage(): string {
    return this.customizedHtml;
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.panelOpen) {
      this.closePanel();
    }
  }

  setAnimation(mode: GlameraAnimationMode): void {
    this.animation = mode;
  }

  setIconPosition(position: GlameraIconPosition): void {
    this.iconPosition = position;
  }

  onAnimationModeEnter(mode: GlameraAnimationMode): void {
    this.hoveredAnimationMode = mode;
  }

  onAnimationModeLeave(): void {
    this.hoveredAnimationMode = null;
  }

  onThemeButtonEnter(theme: 'light' | 'dark'): void {
    this.hoveredThemeButton = theme;
  }

  onThemeButtonLeave(): void {
    this.hoveredThemeButton = null;
  }

  openIconPanel(name: string): void {
    this.selectedIcon = name;
    if (!this.panelOpen) {
      this.lockBodyScroll();
    }
    this.panelOpen = true;
  }

  closePanel(): void {
    if (this.panelOpen) {
      this.panelOpen = false;
      this.unlockBodyScroll();
    }
  }

  setTheme(theme: 'light' | 'dark'): void {
    this.themeManuallySet = true;
    this.theme = theme;
    this.applyTheme(theme);
  }

  setTab(tab: DemoTab): void {
    this.activeTab = tab;
    if (tab === 'docs') {
      this.closePanel();
    }
  }

  iconUsage(name: string): string {
    return `<glamera-${name}-icon animation="hover"></glamera-${name}-icon>`;
  }

  isCopied(key: string): boolean {
    return this.copiedKey === key;
  }

  copyInstallCommand(): void {
    this.copyText(this.installSnippet, 'install');
  }

  copyIconCode(name: string, event: Event): void {
    event.stopPropagation();
    this.copyText(this.iconUsage(name), name);
  }

  copyCustomImport(): void {
    this.copyText(this.customizedImport, 'custom-import');
  }

  copyCustomUsage(): void {
    this.copyText(this.customizedUsage, 'custom-usage');
  }

  setCopyHover(key: string): void {
    this.copyHoverKey = key;
  }

  clearCopyHover(): void {
    this.copyHoverKey = null;
  }

  private applyTheme(theme: 'light' | 'dark'): void {
    document.documentElement.setAttribute('data-theme', theme);
  }

  private copyText(text: string, key: string): void {
    const done = (): void => {
      this.copiedKey = key;
      if (this.copyTimer !== null) {
        clearTimeout(this.copyTimer);
      }
      this.copyTimer = setTimeout(() => {
        this.copiedKey = null;
        this.copyTimer = null;
      }, 1600);
    };

    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(text).then(done).catch(() => this.copyWithFallback(text, done));
      return;
    }

    this.copyWithFallback(text, done);
  }

  ngOnDestroy(): void {
    this.systemThemeQuery?.removeEventListener('change', this.onSystemThemeChange);
    if (this.copyTimer !== null) {
      clearTimeout(this.copyTimer);
    }
    if (this.panelOpen) {
      this.unlockBodyScroll();
    }
  }

  private lockBodyScroll(): void {
    this.bodyScrollY = window.scrollY;
    document.body.classList.add('demo-panel-open');
    document.body.style.top = `-${this.bodyScrollY}px`;
  }

  private unlockBodyScroll(): void {
    document.body.classList.remove('demo-panel-open');
    document.body.style.top = '';
    window.scrollTo(0, this.bodyScrollY);
  }

  private copyWithFallback(text: string, done: () => void): void {
    const input = document.createElement('textarea');
    input.value = text;
    input.setAttribute('readonly', '');
    input.style.position = 'fixed';
    input.style.left = '-9999px';
    document.body.appendChild(input);
    input.select();
    document.execCommand('copy');
    document.body.removeChild(input);
    done();
  }
}
